import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { TreeInput } from '@goaltree/backend/convex/nodes'
import { useAction, useMutation } from 'convex/react'
import type { FunctionArgs } from 'convex/server'
import { ConvexError } from 'convex/values'
import i18next from 'i18next'
import { useState } from 'react'
import { toast } from 'sonner'

import { isConnectionError, withReconnect } from '@/lib/reconnect'
import { handleUsageLimit, openHabitUpsell, planUsageArgs } from '@/lib/usage-limit'

type DecomposeArgs = FunctionArgs<typeof api.onboarding.decomposeGoal>

export function useOnboarding() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [goalLimitReached, setGoalLimitReached] = useState(false)
  // Partial milestones stream under this key while a plan generates.
  const [streamKey, setStreamKey] = useState<string | null>(null)
  const [reconnecting, setReconnecting] = useState(false)

  const decomposeGoalAction = useAction(api.onboarding.decomposeGoal)
  const savePlanMutation = useMutation(api.nodes.savePlan)
  const surpriseMeAction = useAction(api.onboarding.surpriseMe)
  const getLifeCoachSuggestionsAction = useAction(api.onboarding.getLifeCoachSuggestions)

  function extractMessage(err: unknown): string {
    if (err instanceof ConvexError) return String(err.data)
    if (err instanceof Error) return err.message
    return 'Something went wrong. Please try again.'
  }

  async function decompose(
    goalText: string,
    inputs: Pick<
      DecomposeArgs,
      | 'dueDate'
      | 'experienceLevel'
      | 'effortLevel'
      | 'timeAvailable'
      | 'timeBudget'
      | 'seedTasks'
      | 'scheduleDays'
      | 'dayPart'
      | 'details'
      | 'areas'
      | 'fullText'
      | 'otherGoals'
    >,
    // "Second Opinion" refine: hand back the rejected plan + what was wrong.
    refine?: { priorTree: TreeInput; critique: string[]; note?: string },
    // Make it mine: the template plan to adapt.
    draft?: TreeInput,
  ): Promise<TreeInput | null> {
    setIsLoading(true)
    setError(null)
    const key = crypto.randomUUID()
    setStreamKey(key)
    try {
      return (await withReconnect(
        () =>
          decomposeGoalAction({
            goalText,
            streamKey: key,
            ...inputs,
            locale: i18next.language,
            ...planUsageArgs(),
            ...(refine
              ? { priorTree: refine.priorTree, critique: refine.critique, note: refine.note }
              : {}),
            ...(draft ? { draft } : {}),
          }),
        () => setReconnecting(true),
      )) as TreeInput
    } catch (err) {
      console.error('[useOnboarding] decompose error:', err)
      if (handleUsageLimit(err)) return null
      // Raw transport errors ("[CONVEX A(…)] Connection lost…") never reach the user.
      const message =
        err instanceof ConvexError
          ? String(err.data)
          : i18next.t(isConnectionError(err) ? 'planner.connectionLost' : 'planner.buildFailed')
      setError(message)
      toast.error(message)
      return null
    } finally {
      setIsLoading(false)
      setReconnecting(false)
    }
  }

  async function saveTree(
    tree: TreeInput,
    isPublic = true,
    templateId?: Id<'templates'> | null,
  ): Promise<string | null> {
    setIsLoading(true)
    setError(null)
    setGoalLimitReached(false)
    try {
      const { goalId, habitsHeldBack } = await savePlanMutation({
        tree,
        isPublic,
        tzOffsetMin: new Date().getTimezoneOffset(),
        ...(templateId ? { templateId } : {}),
      })
      if (habitsHeldBack > 0)
        toast(i18next.t('habitLimit.heldBack', { count: habitsHeldBack }), {
          description: i18next.t('habitLimit.planNudge', {
            count: tree.habits?.length ?? habitsHeldBack,
          }),
          action: { label: i18next.t('habitLimit.upgrade'), onClick: openHabitUpsell },
          duration: 10_000,
        })
      return goalId
    } catch (err) {
      console.error('[useOnboarding] saveTree error:', err)
      if (err instanceof ConvexError && String(err.data).includes('Free plan is limited')) {
        setGoalLimitReached(true)
        return null
      }
      const message = extractMessage(err)
      setError(message)
      toast.error(message)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  async function surpriseMe(): Promise<string | null> {
    setIsLoading(true)
    setError(null)
    try {
      const result = await surpriseMeAction({ locale: i18next.language, ...planUsageArgs() })
      return result.goalText
    } catch (err) {
      console.error('[useOnboarding] surpriseMe error:', err)
      if (handleUsageLimit(err)) return null
      const message = extractMessage(err)
      setError(message)
      toast.error(message)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  async function getLifeCoachSuggestions(
    lifeArea: string,
    lifestyle: string,
    freeText: string,
    opts?: { name?: string; timeAvailable?: string },
  ): Promise<string[] | null> {
    setIsLoading(true)
    setError(null)
    try {
      const result = await getLifeCoachSuggestionsAction({
        name: opts?.name,
        lifeArea,
        lifestyle,
        freeText,
        timeAvailable: opts?.timeAvailable,
        locale: i18next.language,
        ...planUsageArgs(),
      })
      return result.suggestions
    } catch (err) {
      console.error('[useOnboarding] getLifeCoachSuggestions error:', err)
      if (handleUsageLimit(err)) return null
      const message = extractMessage(err)
      setError(message)
      toast.error(message)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  return {
    decompose,
    saveTree,
    surpriseMe,
    getLifeCoachSuggestions,
    isLoading,
    error,
    goalLimitReached,
    streamKey,
    reconnecting,
  }
}
